/**
 * Voiture de mariage à Cannes — page d'intention manquante.
 *
 * Search Console (3 mois) : « voiture mariage cannes » 150 impressions en
 * position 6,4, « location voiture mariage cannes » 75 en position 4,59,
 * « location de voiture pour mariage cannes » 74 en position 6,3 — soit ~330
 * impressions en page 1, et ZÉRO clic. Un score pareil en position 4-6 ne
 * s'explique pas par le classement : c'est que la page qui remontait n'était
 * pas une page mariage, donc le titre et l'extrait ne répondaient pas à
 * l'intention.
 *
 * Positionnée dans le silo chauffeur (infrastructure trilingue + JSON-LD +
 * sitemap déjà en place), mais le contenu couvre les DEUX offres, parce que
 * les requêtes le font : « location voiture mariage » (sans chauffeur, la
 * voiture des mariés) et « voiture avec chauffeur pour mariage » / « bus
 * mariage cannes » (avec chauffeur, les mariés et les invités).
 *
 * Tous les montants cités viennent des registres réels — `disposalPackages`
 * de VEHICLES (moteur de devis) et `price` de RENTAL_VEHICLES (fiches
 * produit). Aucune prestation inventée : ni décoration, ni champagne, ni
 * conditions d'annulation, qui ne sont documentées nulle part dans le projet.
 */
import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const voitureMariageCannes: ChauffeurPage = {
  slug: "voiture-mariage-cannes",
  title: "Voiture de mariage à Cannes — avec ou sans chauffeur",
  subtitle:
    "Cabriolet pour les mariés, berline avec chauffeur, van ou minibus pour les invités : une seule agence pour toute la logistique du jour J sur la Côte d'Azur.",
  keywords: [
    "voiture mariage Cannes",
    "location voiture mariage Cannes",
    "voiture avec chauffeur mariage",
    "cortège mariage Côte d'Azur",
    "minibus mariage Cannes",
  ],
  hero: {
    title: "Voiture de mariage à Cannes — avec ou sans chauffeur",
    subtitle:
      "Le cabriolet que vous conduisez vous-mêmes, ou le chauffeur qui gère la journée entière pendant que vous profitez de la vôtre. Souvent les deux.",
    keywords: [
      "voiture mariage Cannes",
      "location voiture mariage",
      "chauffeur mariage Côte d'Azur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Véhicule de prestige avec chauffeur pour un mariage à Cannes",
    badges: [
      "Avec ou sans chauffeur",
      "Jusqu'à 16 passagers",
      "5,0/5 · 38 avis Google",
    ],
  },
  benefits: {
    heading: "Pourquoi passer par nous pour le jour J",
    items: [
      {
        title: "Les deux métiers, une seule agence",
        description:
          "Nous louons des véhicules et nous fournissons des chauffeurs. Vous n'avez pas à coordonner un loueur pour la voiture des mariés et une société de VTC pour les invités : un seul interlocuteur, un seul devis.",
      },
      {
        title: "Un chauffeur réservé, pas un trajet",
        description:
          "En mise à disposition, le véhicule et son chauffeur restent avec vous pendant toute la durée retenue. Mairie, photos, église, réception : les étapes s'enchaînent sans repartir sur un nouveau prix à chaque fois.",
      },
      {
        title: "De la citadine au cabriolet de prestige",
        description:
          "Cabriolets Mercedes CLE, Audi A5, BMW Série 4, Maserati GranCabrio : la voiture des mariés se choisit dans la même flotte que celle qu'on loue toute l'année, avec ses prix affichés.",
      },
      {
        title: "Les invités aussi",
        description:
          "Classe V pour 7 passagers, Sprinter VIP jusqu'à 16 : les navettes entre l'hôtel, la cérémonie et la réception se gèrent avec les mêmes chauffeurs, dans la même réservation.",
      },
    ],
  },
  services: {
    heading: "Nos formules pour un mariage",
    services: [
      {
        title: "Mise à disposition des mariés",
        text: "Un chauffeur et son véhicule pour une durée définie, à partir de 3 h. Comptez 240 € les 3 h en Classe E, 450 € en Classe S, 360 € en Classe V. Une journée complète (8 h) va de 600 € à 1 100 € selon la classe.",
      },
      {
        title: "Voiture des mariés sans chauffeur",
        text: "Location d'un cabriolet ou d'une berline de prestige à la journée : à partir de 350 € pour un Mercedes CLE Cabriolet ou un Audi A5 Cab, 450 € pour une BMW Série 4 Cab, 850 € pour un Maserati GranCabrio.",
      },
      {
        title: "Navettes invités",
        text: "Classe V (7 places) ou Sprinter VIP (16 places) avec chauffeur pour les rotations hôtel ⇄ cérémonie ⇄ réception, y compris tard le soir.",
      },
      {
        title: "Transferts aéroport des proches",
        text: "Accueil à l'aéroport de Nice pour la famille et les témoins qui arrivent la veille, à forfait fixé à l'avance — 100 € en Classe E vers Cannes, 130 € en Classe V.",
      },
    ],
  },
  locations: {
    heading: "Où nous intervenons",
    items: [
      {
        name: "Cannes",
        description:
          "Mairie, hôtels de la Croisette, villas des collines et domaines de l'arrière-pays.",
      },
      {
        name: "Antibes & Juan-les-Pins",
        description: "Cap d'Antibes, Port Vauban et domaines viticoles.",
      },
      {
        name: "Saint-Paul-de-Vence & Grasse",
        description: "Bastides, mas provençaux et domaines de l'arrière-pays.",
      },
      {
        name: "Saint-Tropez & le golfe",
        description: "Ramatuelle, Gassin et les plages privées.",
      },
      {
        name: "Monaco & Nice",
        description: "Palaces, salles de réception et aéroport de Nice.",
      },
    ],
  },
  generic: {
    heading: "Combien coûte une voiture de mariage à Cannes ?",
    subheading:
      "Nos tarifs sont ceux affichés sur le site toute l'année : il n'y a pas de grille « mariage ».",
    paragraphs: [
      "Deux logiques de prix cohabitent, selon que vous conduisez ou non. Si vous voulez le volant, c'est une location à la journée : de 250 € pour une Abarth 500 à 350 € pour un cabriolet Mercedes CLE ou Audi A5, 450 € pour une BMW Série 4 Cab, et jusqu'à 850 € pour un Maserati GranCabrio. Le kilométrage inclus est de 200 km par jour sur ces modèles, ce qui couvre largement une journée entre mairie, lieu de photos et réception.",
      "Si vous préférez être conduits, c'est une mise à disposition : le chauffeur et le véhicule vous sont réservés pour une durée choisie, minimum 3 h. Une demi-journée de 4 h coûte 320 € en Classe E, 480 € en Classe V (7 places) et 600 € en Classe S. Une journée de 8 h va de 600 € à 1 100 €. Au-delà du forfait retenu, chaque heure supplémentaire est facturée entre 80 € et 150 € selon la classe.",
      "Beaucoup de couples combinent les deux : le cabriolet en location pour les mariés et les photos, et un Classe V ou un Sprinter VIP avec chauffeur pour faire tourner les invités. Les deux prestations tiennent dans le même devis.",
    ],
    bullets: [
      "Mise à disposition à partir de 240 € (3 h, Classe E)",
      "Location de cabriolet à partir de 350 € la journée",
      "Jusqu'à 16 passagers par véhicule avec le Sprinter VIP",
      "Note 5,0/5 sur 38 avis Google, entreprise active depuis 2022",
    ],
  },
  process: {
    heading: "Comment on s'organise",
    steps: [
      "Dites-nous la date, les lieux (mairie, cérémonie, réception) et le nombre de personnes à déplacer.",
      "Nous proposons la combinaison la plus juste : location seule, mise à disposition, ou les deux, avec le détail des durées.",
      "Vous recevez un devis écrit reprenant chaque véhicule, chaque durée et le prix correspondant.",
      "Le jour J, les véhicules sont sur place aux horaires convenus, et le chauffeur reste joignable toute la prestation.",
    ],
  },
  faq: [
    {
      q: "Quel est le prix d'une voiture de mariage à Cannes ?",
      a: "Sans chauffeur, la location d'un cabriolet démarre à 350 € la journée (Mercedes CLE Cabriolet, Audi A5 Cab), 450 € pour une BMW Série 4 Cab et 850 € pour un Maserati GranCabrio, avec 200 km inclus. Avec chauffeur, la mise à disposition démarre à 240 € pour 3 h en Classe E et 450 € en Classe S ; une journée de 8 h va de 600 € à 1 100 € selon la classe.",
    },
    {
      q: "Peut-on réserver le chauffeur pour toute la journée ?",
      a: "Oui. La mise à disposition se réserve par tranches : 3 h, 4 h, 8 h, 10 h, 12 h ou 14 h. Le chauffeur et le véhicule restent à votre disposition sur toute la durée, sans nouveau calcul de prix entre les étapes. La durée minimum est de 3 h.",
    },
    {
      q: "Combien d'invités pouvez-vous transporter ?",
      a: "Jusqu'à 7 passagers par Mercedes Classe V et jusqu'à 16 par Sprinter VIP. Plusieurs véhicules peuvent être mobilisés sur la même prestation pour un cortège ou des rotations entre l'hôtel, la cérémonie et la réception.",
    },
    {
      q: "Intervenez-vous en dehors de Cannes ?",
      a: "Oui, sur toute la Côte d'Azur : Antibes, Juan-les-Pins, Saint-Paul-de-Vence, Grasse, Nice, Monaco, ainsi que Saint-Tropez et le golfe. Les trajets sans forfait fixe sont estimés au kilomètre, à partir de 2,60 €/km.",
    },
    {
      q: "Peut-on louer la voiture des mariés et un véhicule avec chauffeur en même temps ?",
      a: "Oui, c'est même la combinaison la plus fréquente. Nous exerçons les deux métiers, la location et le transport avec chauffeur : les deux prestations figurent dans le même devis et sont coordonnées par le même interlocuteur.",
    },
    {
      q: "À quel moment faut-il réserver ?",
      a: "Le plus tôt possible pour un mariage en juin, juillet, août ou septembre : ce sont les mois où la flotte et les chauffeurs sont les plus sollicités sur la Côte d'Azur. Nous confirmons chaque réservation par écrit.",
    },
  ],
  relatedLinks: [
    {
      label: "Tarifs de mise à disposition",
      href: "/chauffeur#mise-a-disposition",
    },
    {
      label: "Catalogue des voitures de location",
      href: "/location/vehicules",
    },
    { label: "Transferts avec chauffeur à Cannes", href: "/chauffeur/cannes" },
    { label: "Minibus VIP jusqu'à 16 places", href: "/chauffeur/minibus-vip" },
    { label: "Demander un devis", href: "/contact" },
  ],
  cta: {
    title: "Parlons de votre jour J",
    text: "Donnez-nous la date, les lieux et le nombre d'invités : nous revenons vers vous avec un devis détaillé, véhicule par véhicule.",
    primaryHref: "/contact",
    primaryLabel: "Demander un devis",
    secondaryHref: "/reservation",
    secondaryLabel: "Réserver en ligne",
  },
  seo: {
    title: "Voiture de mariage à Cannes — avec ou sans chauffeur",
    description:
      "Voiture de mariage à Cannes : cabriolet en location dès 350 €/jour, chauffeur à disposition dès 240 €, navettes invités jusqu'à 16 places. Devis détaillé.",
    canonical: "/chauffeur/voiture-mariage-cannes",
  },
};

export default voitureMariageCannes;
